import { useCallback } from 'react';
import mqtt from 'mqtt';

const SENSOR_SETTINGS_PREFIX = 'SensorSettings ';
const RESPONSE_TIMEOUT_MS = 10000;
const GET_SENSOR_SETTINGS_MQTT_COMMAND = 'getSensorSettings'
// scrapp still subscribes to "config" for incoming commands, but publishes cartridge
// replies (SensorSettings, etc.) base64-encoded on "status" instead, to avoid receiving
// its own reply back as a command (see scrapp PR #113, "Send cartridge config replies
// over the existing status topic").
const SENSOR_SETTINGS_RESPONSE_DIRECTION = 'status';

export const useMqttOperations = () => {
  const monitoring_user = process.env.REACT_APP_MONITORING_USER;
  const monitoring_key = process.env.REACT_APP_MONITORING_KEY;
  const mqtt_connection = process.env.REACT_APP_MQTT_CONNECTION;

  const sendMqttMessage = useCallback((unitId, deviceId, direction, message) => {
    return new Promise((resolve, reject) => {
      const topic = `${unitId}/${deviceId}/${direction}`;
      const client = mqtt.connect(mqtt_connection, {
        clientId: 'monitoring_frontend',
        username: monitoring_user,
        password: monitoring_key,
      });

      client.on('connect', () => {
        client.publish(topic, btoa(message), () => {
          console.log(`Message sent to '${topic}': ${message}`);
          client.end();
          resolve();
        });
      });

      client.on('error', (err) => {
        console.error('MQTT error:', err);
        reject(err);
      });
    });
  }, [mqtt_connection, monitoring_user, monitoring_key]);

  // Publishes a command and waits for a response, which may arrive on a different
  // direction topic than the command was sent on (e.g. a device that must avoid
  // echoing its own replies back into the topic it listens for commands on).
  // The response payload is base64-encoded and expected to decode to a known
  // prefix followed by JSON.
  const sendMqttMessageAndWaitForResponse = useCallback((unitId, deviceId, direction, message, responsePrefix, responseDirection = direction) => {
    return new Promise((resolve, reject) => {
      const commandTopic = `${unitId}/${deviceId}/${direction}`;
      const responseTopic = `${unitId}/+/${responseDirection}`;

      const client = mqtt.connect(mqtt_connection, {
        clientId: `monitoring_frontend_${Math.random().toString(16).substring(2, 8)}`,
        username: monitoring_user,
        password: monitoring_key,
      });

      const timer = setTimeout(() => {
        client.end(true);
        reject(new Error(`Timeout waiting for response on topic '${responseTopic}'`));
      }, RESPONSE_TIMEOUT_MS);

      client.on('connect', () => {
        client.subscribe(responseTopic, { qos: 0 }, (err) => {
          if (err) {
            clearTimeout(timer);
            client.end(true);
            reject(err);
            return;
          }
          client.publish(commandTopic, btoa(message), () => {
            console.log(`Message sent to '${commandTopic}': ${message}`);
          });
        });
      });

      client.on('message', (_topic, payload) => {
        const raw = payload.toString();
        let text;
        try {
          text = atob(raw);
        } catch {
          text = raw;
        }
        if (text.startsWith(responsePrefix)) {
          clearTimeout(timer);
          client.end(true);
          try {
            const json = JSON.parse(text.slice(responsePrefix.length));
            resolve(json);
          } catch {
            resolve(text.slice(responsePrefix.length));
          }
        }
      });

      client.on('error', (err) => {
        clearTimeout(timer);
        console.error('MQTT error:', err);
        reject(err);
      });
    });
  }, [mqtt_connection, monitoring_user, monitoring_key]);

  const getSensorSettings = useCallback((unitId, deviceId) => {
    return sendMqttMessageAndWaitForResponse(
      unitId,
      deviceId,
      'config',
      GET_SENSOR_SETTINGS_MQTT_COMMAND,
      SENSOR_SETTINGS_PREFIX,
      SENSOR_SETTINGS_RESPONSE_DIRECTION
    );
  }, [sendMqttMessageAndWaitForResponse]);

  return { sendMqttMessage, getSensorSettings };
};